import React from "react";
import { Box, Button, Typography, Dialog, IconButton, useTheme, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface LoginRequiredProps {
  open: boolean;
  onClose: () => void;
}

// Shared body content — used both inside the Dialog (desktop) and the
// standalone page (mobile) so copy/styles stay in one place.
export const LoginRequiredBody: React.FC<{ onClose?: () => void; redirectTo?: string }> = ({ onClose, redirectTo }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Use the explicit redirectTo if provided (passed from the page route),
  // otherwise fall back to current URL (used from the desktop dialog).
  const resolvedRedirect = redirectTo ?? (window.location.pathname + window.location.search);

  const goToLogin = () => {
    if (onClose) onClose();
    navigate(`/login?redirect=${encodeURIComponent(resolvedRedirect)}`);
  };

  const goToSignup = () => {
    if (onClose) onClose();
    navigate(`/register?redirect=${encodeURIComponent(resolvedRedirect)}`);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      textAlign="center"
      px={3}
      pb={4}
      pt={2}
      sx={{ flex: 1, justifyContent: "center" }}
    >
      {/* Waving hand in teal disc */}
      <Box
        sx={{
          width: 96,
          height: 96,
          borderRadius: "50%",
          bgcolor: "rgba(1,175,174,0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2.5,
          boxShadow: "0 8px 22px rgba(1,175,174,.16)",
        }}
      >
        <Box
          component="span"
          role="img"
          aria-label="waving hand"
          sx={{
            fontSize: 48,
            lineHeight: 1,
            display: "inline-block",
            "@keyframes oot-wave": {
              "0%, 60%, 100%": { transform: "rotate(0deg)" },
              "15%": { transform: "rotate(18deg)" },
              "30%": { transform: "rotate(-10deg)" },
              "45%": { transform: "rotate(14deg)" },
            },
            animation: "oot-wave 1.6s ease-in-out infinite",
            transformOrigin: "70% 70%",
          }}
        >
          👋
        </Box>
      </Box>

      <Typography sx={{ fontWeight: 800, fontSize: 21, color: "text.primary", lineHeight: 1.25 }}>
        {t("loginRequired.title")}
      </Typography>

      <Typography sx={{ fontSize: 14.5, color: "text.secondary", mt: 1, mb: 3, lineHeight: 1.5, maxWidth: 260 }}>
        {t("loginRequired.subtitle")}
      </Typography>

      {/* Log in button */}
      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={goToLogin}
        sx={{
          borderRadius: "14px",
          height: 50,
          fontSize: 15,
          fontWeight: 700,
          textTransform: "none",
          maxWidth: 320,
          boxShadow: "0 8px 18px rgba(1,175,174,.28)",
        }}
      >
        {t("loginRequired.loginButton")}
      </Button>

      {/* Sign up link */}
      <Typography sx={{ mt: 2, fontSize: 14, color: "text.secondary" }}>
        {t("loginRequired.newUserPrompt")}{" "}
        <Box
          component="span"
          onClick={goToSignup}
          sx={{ color: "primary.main", fontWeight: 700, cursor: "pointer" }}
        >
          {t("loginRequired.signupLink")}
        </Box>
      </Typography>
    </Box>
  );
};

// On xs/sm: navigate to /login-required (a real route inside BaseLayout so
// Header + Footer are always present). On md+: show a centered Dialog.
const LoginRequired: React.FC<LoginRequiredProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  React.useEffect(() => {
    if (open && isMobile) {
      onClose(); // close the "dialog" state immediately
      // replace: true so pressing back skips this screen entirely
      navigate(`/login-required?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`, { replace: true });
    }
  }, [open, isMobile, navigate, onClose]);

  // On mobile we navigate away — nothing to render here
  if (isMobile) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      slotProps={{
        paper: {
          sx: {
            borderRadius: "20px",
            p: 0,
            overflow: "hidden",
          },
        },
      }}
    >
      {/* Close button */}
      <Box display="flex" justifyContent="flex-end" px={1.5} pt={1.5}>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Box>
      <LoginRequiredBody onClose={onClose} />
    </Dialog>
  );
};

export default LoginRequired;
